// Viewport observer utilities for CSS class toggling and animation control on visibility changes.
// Attributes supported on observed elements:
// - data-viewport-class: class to add when visible
// - data-viewport-class-remove: class to remove when not visible
// - data-viewport-animation: optional; "pause" or "reset"/"cancel" when not visible (aliases supported)
//   Semantics: when out of view -> pause; when back in view and "reset" -> rewind to 0 then play.

export function inViewPort(element: HTMLElement, blazorComponent: any, callbackMethodName: String) {
	const observer = new IntersectionObserver((entries) => {
		for (const entry of entries) {
			const visible = entry.isIntersecting;
			blazorComponent.invokeMethodAsync(callbackMethodName as any, visible);
		}
	});
	observer.observe(element);
}

// Constants
const OBSERVED_ATTR = 'data-viewport-observer';
const OBSERVE_SELECTOR = '[data-viewport-class], [data-viewport-class-remove], [data-viewport-animation]';

// State
const lastState = new WeakMap<Element, 'in' | 'out'>();
let sharedObserver: IntersectionObserver | null = null;

type AnimMode = 'pause' | 'reset' | null;

// Animation helpers
function getAnimationsDeep(node: Element): any[] {
	try {
		const fn = (node as any).getAnimations;
		if (typeof fn === 'function') {
			try { return fn.call(node, { subtree: true }); } catch { return fn.call(node); }
		}
	} catch {}
	return [];
}

// Pause helper used when element goes OUT of view
function controlAnimations(el: HTMLElement, action: 'pause' | 'reset') {
	try {
		const animations = getAnimationsDeep(el);
		for (const anim of animations) {
			// Out-of-view behavior: only pause. Do NOT reset to 0 here.
			if (action === 'pause' || action === 'reset') {
				anim.pause();
			}
		}
	} catch { /* no-op */ }
}

// Rewind to start (used when element comes IN to view and mode is "reset")
function resetAnimations(el: HTMLElement) {
	try {
		const animations = getAnimationsDeep(el);
		for (const anim of animations) {
			try { anim.currentTime = 0; } catch { /* no-op */ }
		}
	} catch { /* no-op */ }
}

// Resume or start animations (paused or idle), avoid replaying finished ones unless reset moved time back.
function playAnimations(el: HTMLElement) {
	try {
		const animations = getAnimationsDeep(el);
		for (const anim of animations) {
			try {
				const timing = anim.effect?.getComputedTiming?.();
				const hasFinished = timing && anim.currentTime !== null && anim.currentTime >= timing.endTime;

				// Start if paused or idle. If finished, only play if not actually at/after end (e.g., after reset to 0).
				if ((anim.playState === 'paused' || anim.playState === 'idle') && !hasFinished) {
					anim.play();
				}
			} catch { /* no-op */ }
		}
	} catch { /* no-op */ }
}

function parseAnimMode(value: string | null): AnimMode {
	if (value == null) return null;
	const v = value.trim().toLowerCase();
	// Back-compat aliases:
	// - "play" meant "pause when out of view"
	// - "cancel" meant "reset when out of view" (now: reset when back in view)
	if (v === 'pause' || v === 'play') return 'pause';
	if (v === 'reset' || v === 'cancel') return 'reset';
	return null;
}

function getSharedObserver(): IntersectionObserver {
	if (sharedObserver) return sharedObserver;

	sharedObserver = new IntersectionObserver((entries) => {
		for (const entry of entries) {
			const el = entry.target as HTMLElement;
			const addClass = el.getAttribute('data-viewport-class');
			const removeClass = el.getAttribute('data-viewport-class-remove');
			const animMode = parseAnimMode(el.getAttribute('data-viewport-animation'));

			const state: 'in' | 'out' = entry.isIntersecting ? 'in' : 'out';
			const prev = lastState.get(el);

			if (prev === state) continue;

			lastState.set(el, state);

			if (state === 'in') {
				if (addClass) el.classList.add(addClass);
				if (animMode === 'reset') {
					resetAnimations(el);
					playAnimations(el);
				} else if (animMode === 'pause') {
					playAnimations(el);
				}
			} else {
				if (removeClass) el.classList.remove(removeClass);
				if (animMode != null) controlAnimations(el, animMode);
			}
		}
	});

	return sharedObserver;
}

// Update the ViewportObserver function
export function ViewportObserver() {
	const observer = getSharedObserver();
	const elements = document.querySelectorAll(OBSERVE_SELECTOR);

	for (let i = 0; i < elements.length; i++) {
		const element = elements[i] as HTMLElement;
		if (element.getAttribute(OBSERVED_ATTR) !== 'true') {
			element.setAttribute(OBSERVED_ATTR, 'true');
			observer.observe(element);
		}
	}
}
